import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { Paginator } from 'primereact/paginator';

const ScienceScoringListing = ({
    data = [],
    activeTab,
    onEdit,
    onDelete,
    onSubmitStatus,
    onCancelSubmit,
    onBulkSend,
    onBulkCancel,
    onBulkDelete,
    isLoading,
    isViewOnly = false
}) => {
    const menu = useRef(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    const [first, setFirst] = useState(0);
    const rows = 20;

    const containerRef = useRef(null);
    const scrollLeftRef = useRef(0);

    const handleScroll = (e) => {
        scrollLeftRef.current = e.target.scrollLeft;
    };

    useLayoutEffect(() => {
        if (containerRef.current && scrollLeftRef.current > 0) {
            containerRef.current.scrollLeft = scrollLeftRef.current;
        }
    }, [data, first]);

    useEffect(() => {
        setFirst(0);
        setSelectedIds([]);
    }, [activeTab]);

    useEffect(() => {
        setSelectedIds(prev => prev.filter(id => data.some(d => d.Id === id)));

        if (data.length > 0 && first >= data.length) {
            setFirst(Math.max(0, Math.floor((data.length - 1) / rows) * rows));
        } else if (data.length === 0) {
            setFirst(0);
        }
    }, [data, first, rows]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(data.map(item => item.Id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (e, id) => {
        if (e.target.checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(itemId => itemId !== id));
        }
    };

    const paginatedData = useMemo(() => data.slice(first, first + rows), [data, first, rows]);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Đã duyệt': return { backgroundColor: '#27ae60', color: '#fff' };
            case 'Đã gửi duyệt': return { backgroundColor: '#3498db', color: '#fff' };
            case 'Từ chối': return { backgroundColor: '#e74c3c', color: '#fff' };
            case 'Yêu cầu chỉnh sửa': return { backgroundColor: '#9b59b6', color: '#fff' };
            case 'Đang chỉnh sửa':
            case 'Lưu nháp': return { backgroundColor: '#f39c12', color: '#fff' };
            case 'Tham khảo': return { backgroundColor: '#17a2b8', color: '#fff' };
            default: return { backgroundColor: '#95a5a6', color: '#fff' };
        }
    };

    const itemTemplate = (item, options) => {
        return (
            <div className={options.className} onClick={(e) => {
                if (e.originalEvent) e.originalEvent.preventDefault();
                options.onClick(e);
            }}
                style={{
                    display: 'flex', alignItems: 'center', padding: '10px 15px',
                    pointerEvents: item.disabled ? 'none' : 'auto',
                    opacity: item.disabled ? 0.5 : 1,
                    transition: 'background-color 0.2s',
                    cursor: item.disabled ? 'default' : 'pointer'
                }}>
                <i className={item.icon} style={{ color: item.color, width: '24px', fontSize: '1.1rem', marginRight: '8px' }}></i>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#495057' }}>{item.label}</span>
            </div>
        );
    };

    const getMenuItems = () => {
        if (!selectedItem) return [];

        const isLocked = selectedItem.Status === 'Đã gửi duyệt' || selectedItem.Status === 'Đã duyệt';
        const isReference = selectedItem.Status === 'Tham khảo';
        const items = [];

        if (!isReference) {
            if (selectedItem.Status === 'Đang chỉnh sửa' || selectedItem.Status === 'Lưu nháp' || selectedItem.Status === 'Từ chối' || selectedItem.Status === 'Yêu cầu chỉnh sửa') {
                items.push({ label: 'Gửi duyệt', icon: 'fa-solid fa-paper-plane', color: '#3498db', command: () => onSubmitStatus && onSubmitStatus(selectedItem.Id), template: itemTemplate });
            } else if (selectedItem.Status === 'Đã gửi duyệt' && onCancelSubmit) {
                items.push({ label: 'Hủy gửi duyệt', icon: 'fa-solid fa-rotate-left', color: '#f39c12', command: () => onCancelSubmit(selectedItem.Id), template: itemTemplate });
            }

            items.push({ separator: true });
            items.push({ label: 'Chỉnh sửa', icon: 'fa-solid fa-pen-to-square', color: '#3498db', disabled: isLocked, command: () => onEdit(selectedItem), template: itemTemplate });
        } else {
            items.push({ label: 'Xem chi tiết', icon: 'fa-solid fa-eye', color: '#3498db', command: () => onEdit(selectedItem), template: itemTemplate });
            items.push({ separator: true });
        }

        items.push({ label: 'Xóa', icon: 'fa-solid fa-trash-can', color: '#e74c3c', disabled: isLocked, command: () => onDelete(selectedItem.Id), template: itemTemplate });

        return items;
    };

    const onPageChange = (event) => {
        setFirst(event.first);
    };

    return (
        <>
            {!isViewOnly && selectedIds.length > 0 && (
                <div style={{
                    padding: '12px 16px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: '#e0f2fe', padding: '8px', borderRadius: '50%', display: 'flex', width: '36px', height: '36px', justifyContent: 'center', alignItems: 'center' }}>
                            <i className="fa-solid fa-list-check" style={{ color: '#0284c7', fontSize: '16px' }}></i>
                        </div>
                        <span style={{ fontSize: '15px', color: '#475569' }}>
                            Đã chọn <strong style={{ color: '#0f172a', fontSize: '18px', margin: '0 4px' }}>{selectedIds.length}</strong> mục
                        </span>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {activeTab !== 'Reference' && (
                            <>
                                <button
                                    onClick={() => onBulkSend && onBulkSend(selectedIds)}
                                    style={{
                                        backgroundColor: '#3b82f6', color: 'white', border: 'none',
                                        padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                        fontWeight: '500', cursor: 'pointer', display: 'flex',
                                        alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <i className="fa-solid fa-paper-plane" style={{ fontSize: '12px' }}></i> Gửi duyệt
                                </button>

                                <button
                                    onClick={() => onBulkCancel && onBulkCancel(selectedIds)}
                                    style={{
                                        backgroundColor: '#f59e0b', color: 'white', border: 'none',
                                        padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                        fontWeight: '500', cursor: 'pointer', display: 'flex',
                                        alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#d97706'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f59e0b'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <i className="fa-solid fa-rotate-left" style={{ fontSize: '12px' }}></i> Hủy
                                </button>
                            </>
                        )}

                        <button
                            onClick={() => onBulkDelete && onBulkDelete(selectedIds)}
                            style={{
                                backgroundColor: '#ef4444', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-trash-can" style={{ fontSize: '12px' }}></i> Xóa
                        </button>
                    </div>
                </div>
            )}

            <div
                className="table-card"
                ref={containerRef}
                onScroll={handleScroll}
                style={{
                    overflowX: 'auto',
                    paddingBottom: '10px',
                    position: 'relative'
                }}
            >
                {isLoading && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        zIndex: 10, display: 'flex', justifyContent: 'center', paddingTop: '60px'
                    }}>
                        <i className="fa-solid fa-circle-notch fa-spin fa-2x" style={{ color: '#3498db', marginRight: '10px' }}></i>
                    </div>
                )}

                {data.length === 0 && !isLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                        <i className="fa-solid fa-calculator" style={{ fontSize: '60px', color: '#5dade2', marginBottom: '15px' }}></i>
                        <h3 style={{ color: '#e74c3c', margin: '0 0 10px 0' }}>Không tìm thấy dữ liệu</h3>
                        <p style={{ margin: 0, fontSize: '14px' }}>Chưa có bảng kê tính điểm nào được tạo hoặc khớp với điều kiện lọc</p>
                    </div>
                ) : (
                    <>
                        <table className="custom-table" style={{ minWidth: '1750px' }}>
                            <thead>
                                <tr>
                                    {!isViewOnly && (
                                        <th width="3%" style={{ textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                checked={data.length > 0 && selectedIds.length === data.length}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                    )}
                                    <th width="3%" style={{ textAlign: 'center' }}>STT</th>
                                    <th width="7%">CHỨC DANH</th>
                                    <th width="7%" style={{ textAlign: 'center' }}>ĐỊNH MỨC CHUẨN (GIỜ)</th>
                                    <th width="6%" style={{ textAlign: 'center' }}>TỈ LỆ GIẢM (%)</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>CẦN THỰC HIỆN (GIỜ)</th>
                                    <th width="10%" style={{ textAlign: 'center' }}>THỰC HIỆN ĐƯỢC (ĐIỂM / GIỜ)</th>
                                    <th width="5%" style={{ textAlign: 'center' }}>HỆ SỐ</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>NCKH NĂM TRƯỚC (GIỜ)</th>
                                    <th width="7%" style={{ textAlign: 'center' }}>CHÊNH LỆCH</th>
                                    <th width="6%" style={{ textAlign: 'center' }}>TỪ NGÀY</th>
                                    <th width="6%" style={{ textAlign: 'center' }}>ĐẾN NGÀY</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>NGÀY CẬP NHẬT</th>
                                    <th width="8%">GHI CHÚ</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>TRẠNG THÁI</th>
                                    {!isViewOnly && <th width="5%" style={{ textAlign: 'center' }}>CHỨC NĂNG</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.map((item, index) => {
                                    const actualIndex = first + index + 1;
                                    return (
                                        <tr key={item.Id} style={{ backgroundColor: selectedIds.includes(item.Id) ? '#f4f9ff' : 'transparent' }}>
                                            {!isViewOnly && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                        checked={selectedIds.includes(item.Id)}
                                                        onChange={(e) => handleSelectRow(e, item.Id)}
                                                    />
                                                </td>
                                            )}
                                            <td style={{ textAlign: 'center' }}>{actualIndex}</td>
                                            <td style={{ fontWeight: 'bold' }}>{item.JobTitle}</td>
                                            <td style={{ textAlign: 'center' }}>{item.StandardHours}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                {Math.round(Number(item.ReductionPercentage) * 100) / 100}
                                            </td>
                                            <td style={{ textAlign: 'center', fontWeight: '500' }}>
                                                {Math.round(Number(item.RequiredHours) * 100) / 100}
                                            </td>
                                            <td style={{ textAlign: 'center', color: '#0056b3', fontWeight: 'bold' }}>
                                                {Math.round(Number(item.AchievedScore) * 100) / 100} / {Math.round(Number(item.ConvertedHours) * 100) / 100}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{item.Coefficient}</td>
                                            <td style={{ textAlign: 'center' }}>{item.PreviousYearHours || 0}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: item.DifferenceHours >= 0 ? '#28a745' : '#dc3545' }}>
                                                {Math.round(Number(item.DifferenceHours) * 100) / 100}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{item.FromDate}</td>
                                            <td style={{ textAlign: 'center' }}>{item.ToDate}</td>
                                            <td style={{ textAlign: 'center', fontSize: '13px', color: '#666' }}>
                                                {item.UpdatedDate || '---'}
                                            </td>
                                            <td style={{
                                                wordBreak: 'break-word',
                                                color: (item.Status === 'Từ chối' || item.Status === 'Yêu cầu chỉnh sửa') ? '#e74c3c' : 'inherit'
                                            }}>
                                                {item.Note || '---'}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span style={{
                                                    ...getStatusStyle(item.Status),
                                                    padding: '4px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    whiteSpace: 'nowrap',
                                                    display: 'inline-block'
                                                }}>
                                                    {item.Status}
                                                </span>
                                            </td>
                                            {!isViewOnly && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <Button
                                                        icon="fa-solid fa-ellipsis-vertical"
                                                        className="p-button-rounded p-button-text p-button-plain"
                                                        aria-label="Thao tác"
                                                        style={{ color: '#6c757d', width: '2.5rem', height: '2.5rem' }}
                                                        onClick={(event) => {
                                                            setSelectedItem(item);
                                                            menu.current.toggle(event);
                                                        }}
                                                        aria-controls="popup_menu"
                                                        aria-haspopup
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {data.length > rows && (
                            <div style={{ marginTop: '15px', borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                                <Paginator
                                    first={first}
                                    rows={rows}
                                    totalRecords={data.length}
                                    onPageChange={onPageChange}
                                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                    style={{ background: 'transparent', border: 'none' }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {!isViewOnly && (
                <Menu model={getMenuItems()} popup ref={menu} id="popup_menu" appendTo={document.body} style={{ width: '220px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
            )}
        </>
    );
};

export default ScienceScoringListing;